import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { localStoragePrefix } from "./config";

export function useLocalStorage<T>(
  key: string,
  defaultValue?: T
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] {
  const localStorageKey = `${localStoragePrefix}${key}`;
  const [value, setValue] = useState<T | undefined>(
    load(localStorageKey, defaultValue)
  );

  useEffect(
    () => setValue(load(localStorageKey, defaultValue)),
    [localStorageKey, defaultValue]
  );

  const doSetValue: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (updater) => {
      const nextValue =
        typeof updater === "function"
          ? (updater as (prevState: T | undefined) => T | undefined)(value)
          : updater;

      if (nextValue !== undefined) {
        localStorage.setItem(localStorageKey, JSON.stringify(nextValue));
      } else {
        localStorage.removeItem(localStorageKey);
      }

      setValue(nextValue);
    },
    [localStorageKey, value]
  );

  return [value, doSetValue];
}

function load<T>(key: string, defaultValue?: T): T | undefined {
  const value = localStorage.getItem(key);
  if (value !== null) return JSON.parse(value);
  return defaultValue;
}
